import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { workspaceName } from '../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useCloudPlatformDiscovery } from '../utils/hooks';
import { ListSecretsRequest, ListSecretsResponse } from './gen/ai/h2o/securestore/v1/secret_service_pb';
import { RevealSecretVersionValueRequest } from './gen/ai/h2o/securestore/v1/secret_version_service_pb';
import { listSecrets, revealSecretVersionValue } from './secure-store.secrets.functions';

export const cacheKeys = {
  secretsAll: (workspaceId: string, params?: Pick<ListSecretsRequest, 'showDeleted'>) => {
    const baseKey: QueryKey = [`secure-store`, workspaceId, `secrets`, `all`];
    if (!params) return baseKey;
    const { showDeleted } = params;
    return baseKey.concat({ showDeleted: showDeleted || false });
  },
};

export const useQuerySecrets = <TData = ListSecretsResponse>(
  workspaceId: string,
  options?: Omit<UseQueryOptions<ListSecretsResponse, unknown, TData>, 'queryFn' | 'queryKey'>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const secureStoreApiUrl = cloudPlatformDiscovery?.secureStore || '';
  const request: ListSecretsRequest = {
    parent: workspaceName.compile({ workspace: workspaceId }),
    pageSize: 1000,
    showDeleted: false, // Only interested in active secrets.
  };

  const baseOptions: UseQueryOptions<ListSecretsResponse, unknown, TData> = {
    queryKey: cacheKeys.secretsAll(workspaceId, { showDeleted: false }),
    queryFn: ({ signal }) => {
      return listSecrets(secureStoreApiUrl, request, { signal });
    },
    enabled: !!workspaceId && (options?.enabled ?? true),
  };

  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export const useRevealSecretVersionValue = () => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const secureStoreUrl = cloudPlatformDiscovery?.secureStore || '';

  return (request: RevealSecretVersionValueRequest) => {
    return revealSecretVersionValue(secureStoreUrl, request);
  };
};
